import { Form, Input, Modal, Select, Tooltip } from "antd";
import { TModalFilterState, TSearchValue } from "./search-template";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { getMemberColor } from "@src/util/intelligence-search/get-member-info";
import React from "react";
import {
    renderInputByDataTypeAndOperator,
    TSearchFilterFormValueProps,
} from "@src/util/intelligence-search/render-operator-input-with-form";
import {
    OPERATOR_LABEL_MAP,
    operatorIconMap,
    operatorMap,
} from "@src/pages/intelligence-search/constant/filter-operator";
import { SmlAsset, DataType } from "@src/types/intelligence-search";
import { OperatorEnum } from "@src/pages/intelligence-search/enum";

export const SearchFilterModal: React.FC<{
    values: TSearchValue[];
    openFilterModalState: TModalFilterState;
    handleComfirmFilterModal: (meta: SmlAsset, filter: TSearchValue["filter"]) => void;
    handleCloseFilterModal: () => void;
    searchModalFilterValueProps?: TSearchFilterFormValueProps;
}> = ({
    values,
    openFilterModalState,
    handleComfirmFilterModal,
    handleCloseFilterModal,
    searchModalFilterValueProps,
}) => {
    const [meta, setMeta] = React.useState<SmlAsset>();
    const [form] = Form.useForm();
    const filterValueWatch = Form.useWatch("filter-value", form);
    const filterOperatorWatch = Form.useWatch("filter-operator", form);
    React.useEffect(() => {
        if (!openFilterModalState.open) return;
        setMeta(openFilterModalState.meta);
    }, [form, openFilterModalState, values]);

    React.useEffect(() => {
        if (!meta?.name) return;
        const initFilter = values.find((v) => v?.meta?.name === meta?.name);
        const timeoutId = setTimeout(() => {
            form.setFieldsValue({
                "filter-name": meta?.name,
                "filter-operator": initFilter?.filter?.operator ?? OperatorEnum.EQ,
                "filter-value": initFilter?.filter?.value,
            });
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [meta, values, form]);

    if (!meta?.name) {
        return null;
    }

    const { open, name } = openFilterModalState;
    const { data_type, asset_type, description, title } = meta;

    const operators = operatorMap[data_type as DataType];

    return (
        <Modal
            open={!!open}
            width={1000}
            centered
            title={
                <div className="flex flex-col">
                    <div>
                        <span
                            style={{
                                color: getMemberColor(asset_type).textColor,
                            }}
                        >
                            {title}
                        </span>
                        <Tooltip
                            title={
                                <div className="flex flex-col">
                                    <span>Name : {name}</span>
                                    <span>Data type : {data_type}</span>
                                    <span>Asset type : {asset_type}</span>
                                </div>
                            }
                        >
                            <ExclamationCircleOutlined className="ml-2 text-gray-600" />
                        </Tooltip>
                    </div>
                    <span className="text-[12px] text-gray-400 font-normal">{description}</span>
                </div>
            }
            onCancel={handleCloseFilterModal}
            onOk={() => {
                form.validateFields().then((values) => {
                    handleComfirmFilterModal(meta, {
                        name: name,
                        value: values["filter-value"],
                        operator: values["filter-operator"],
                    });
                });
            }}
        >
            <Form form={form}>
                <div
                    css={css`
                        .ant-picker {
                            background-color: white;
                        }
                    `}
                    className="grid grid-cols-7 pt-4 space-x-2"
                >
                    <div className="col-span-2">
                        <Form.Item name="filter-name">
                            <Input disabled />
                        </Form.Item>
                    </div>
                    <div className="col-span-2">
                        <Form.Item
                            name="filter-operator"
                            required
                            rules={[
                                {
                                    validator: (rule, value) => {
                                        if (!value || value.length === 0)
                                            return Promise.reject("Please input filter operator!");
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Select
                                style={{ width: "100%" }}
                                options={operators.map((_operator) => ({
                                    value: _operator,
                                    label: (
                                        <div className="flex items-center ">
                                            <span className="translate-y-1">{operatorIconMap[_operator]}</span>
                                            <span className="lowercase ml-2">{OPERATOR_LABEL_MAP[_operator]}</span>
                                        </div>
                                    ),
                                }))}
                            />
                        </Form.Item>
                    </div>
                    <div className="col-span-3">
                        <Form.Item name="filter-value" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item label="">
                            {renderInputByDataTypeAndOperator({
                                value: filterValueWatch,
                                onChange: (val) => form.setFieldValue("filter-value", val),
                                operator: filterOperatorWatch,
                                dataType: data_type,
                                assetMeta: meta as any,
                                ...searchModalFilterValueProps,
                            })}
                        </Form.Item>
                    </div>
                </div>
            </Form>
        </Modal>
    );
};
